import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ApiService } from "../services/api";
import { useModal } from "../hooks/useModal";
import {
  getAllowedYesToolsClassOptions,
  getYesToolsClassSelectionError,
  isYesToolsGraduateTrackRequired,
  isYesToolsHostname,
  normalizeYesToolsClassValue,
  YES_TOOLS_GRADUATE_TRACK_OPTIONS,
} from "../utils/yesTools";
import { readRememberedReferralCode, rememberReferralCode } from "../utils/referral";
import { AydinlatmaPenceresi } from "./AydinlatmaMetni";

export default function RegisterPage() {
  // Davet linkiyle (/register?davet=YES-XXXXX) gelindiyse kod kayıtta hesaba yazılır.
  const [searchParams] = useSearchParams();
  const invitedCode = (searchParams.get("davet") || "").trim().toUpperCase();
  const [referralCode, setReferralCode] = useState(() => invitedCode || readRememberedReferralCode());
  useEffect(() => {
    if (invitedCode) rememberReferralCode(invitedCode);
  }, [invitedCode]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [selectedClassLevel, setSelectedClassLevel] = useState("");
  const [selectedGraduateTrack, setSelectedGraduateTrack] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [registrationDepartments, setRegistrationDepartments] = useState<{ id: string; name: string }[]>([]);
  // KVKK (28 Eki — sahibin isteği): aydınlatma metni onayı ZORUNLU, işaretsiz kayıt olunmaz; SMS, e-posta, WhatsApp ve telefonla
  // bilgilendirme bu metinde yazar, ayrıca sorulmaz. Kampanya iletisi onayı ayrı ve isteğe bağlı (6563 + İYS: zorunlu yapılamaz)
  const [whatsapp, setWhatsapp] = useState<{ acik: boolean; numara?: string | null }>({ acik: false });
  const [kvkkOnay, setKvkkOnay] = useState(false);
  const [pazarlamaOnay, setPazarlamaOnay] = useState(false);
  const [aydinlatmaAcik, setAydinlatmaAcik] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const { register, loading, systemConfig } = useAuth();
  const { showAlert } = useModal();
  const navigate = useNavigate();
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  // Sistem ayarında "kayıtta görünecek departmanlar" tanımlıysa: departman-seçimli mod
  // (sınıf/alan/doğum tarihi sorulmaz). Tanımlı değilse eski sınıf-akışı (geriye uyum).
  const departmentMode = isYesToolsDomain && registrationDepartments.length > 0;
  const allowedClassOptions = getAllowedYesToolsClassOptions(birthDate);
  const shouldShowGraduateTrackSelect = isYesToolsGraduateTrackRequired(selectedClassLevel);
  const classSelectionError = getYesToolsClassSelectionError(
    birthDate,
    selectedClassLevel,
    selectedGraduateTrack
  );

  // Kayıtta görünecek departmanları çek (varsa departman-seçimli moda geçilir)
  useEffect(() => {
    if (!isYesToolsDomain) return;
    ApiService.getRegistrationDepartments()
      .then((res) => {
        setRegistrationDepartments(res?.departments || []);
        setWhatsapp(res?.whatsapp || { acik: false });
      })
      .catch(() => setRegistrationDepartments([]));
  }, [isYesToolsDomain]);

  useEffect(() => {
    if (systemConfig && systemConfig.isRegistrationEnabled === false) {
      navigate("/login");
    }
  }, [systemConfig, navigate]);

  const resetTurnstile = useCallback(() => {
    const w = window as any;
    if (w.turnstile && turnstileWidgetId.current !== null) {
      w.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken("");
  }, []);

  useEffect(() => {
    const w = window as any;

    const render = () => {
      if (!turnstileRef.current || turnstileWidgetId.current !== null) return;
      turnstileWidgetId.current = w.turnstile.render(turnstileRef.current, {
        sitekey: "0x4AAAAAADeL02opQoKacfFr",
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
        theme: "light",
        language: "tr",
        size: "flexible",
      });
    };

    if (w.turnstile) {
      render();
    } else {
      w.onloadTurnstileCallback = render;
    }

    return () => {
      if (w.turnstile && turnstileWidgetId.current !== null) {
        w.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const formatPhoneNumber = (value: string) => {
    // Başta +90 / 0 yazılırsa atılır; yoksa 10 hane sınırı numaranın son hanesini kesiyordu.
    let digits = value.replace(/\D/g, "");
    if (digits.startsWith("90") && digits.length > 10) digits = digits.slice(2);
    digits = digits.replace(/^0+/, "");

    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;

    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!turnstileToken) {
      const msg = "Lütfen robot olmadığınızı doğrulayın";
      setError(msg);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    // YES.Tools bireysel kayıtta doğum tarihi İSTEĞE BAĞLI (sınıf seçimi yeter);
    // diğer akışlarda (cafe) departman kararı için hâlâ zorunlu
    if (!firstName || !lastName || !email || (!isYesToolsDomain && !birthDate) || !password || !rePassword) {
      const msg = "Tüm alanlar zorunludur";
      setError(msg);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    if (departmentMode && !selectedDepartmentId) {
      const msg = "Lütfen bir departman seçin";
      setError(msg);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    if (isYesToolsDomain && !departmentMode && !selectedClassLevel) {
      const msg = "YES.Tools için sınıf seçimi zorunludur";
      setError(msg);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    if (isYesToolsDomain && !departmentMode && classSelectionError) {
      setError(classSelectionError);
      await showAlert({ title: "Kayıt Hatası", message: classSelectionError, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    if (password !== rePassword) {
      const msg = "Şifreler eşleşmiyor";
      setError(msg);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      setSubmitting(false);
      return;
    }

    if (password.length < 8) {
      const msg = "Şifre en az 8 karakter olmalıdır";
      setError(msg);
      setSubmitting(false);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      return;
    }

    if (!kvkkOnay) {
      const msg = "Kayıt için Aydınlatma Metni'ni okuyup onay kutusunu işaretlemeniz gerekir";
      setError(msg);
      setSubmitting(false);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      return;
    }

    const cleanPhone = phone.replace(/\D/g, "");

    // yes.tools kaydında telefon zorunlu: kurum dışı deneme öğrencilerine ve davetlere telefondan ulaşılıyor.
    if (isYesToolsDomain && !/^5\d{9}$/.test(cleanPhone)) {
      const msg = cleanPhone ? "Geçerli bir cep telefonu girin (5XX XXX XX XX)" : "Telefon numarası zorunludur";
      setError(msg);
      setSubmitting(false);
      await showAlert({ title: "Kayıt Hatası", message: msg, type: "error", buttonText: "Tamam" });
      return;
    }

    try {
      const response = await register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        rePassword,
        birthDate: departmentMode ? undefined : birthDate || undefined,
        parentEmail: isYesToolsDomain ? parentEmail.trim() || undefined : undefined,
        phone: cleanPhone || undefined,
        selectedClassLevel: isYesToolsDomain && !departmentMode ? selectedClassLevel : undefined,
        selectedGraduateTrack:
          isYesToolsDomain && !departmentMode && shouldShowGraduateTrackSelect ? selectedGraduateTrack : undefined,
        selectedDepartmentId: departmentMode ? selectedDepartmentId : undefined,
        referralCode: referralCode.trim() || undefined,
        kvkkOnay: true,
        pazarlamaOnay,
        turnstileToken,
      });

      if ((response as any)?.requiresPhoneVerification) {
        const verifyState = {
          userId: (response as any).userId,
          phone: (response as any).phone,
          fromLogin: false,
        };

        await showAlert({
          title: 'Kayıt Başarılı!',
          message:
            'Hesabınız oluşturuldu. Telefonunuza gönderilen doğrulama kodunu girerek hesabınızı aktif edin.',
          type: 'success',
          buttonText: 'Doğrulamaya Git',
        });

        navigate('/verify-phone', { state: verifyState });
      } else {
        await showAlert({
          title: "Kayıt Başarılı! 🎉",
          message:
            "Hesabınız başarıyla oluşturuldu. E-posta adresinize gönderilen doğrulama bağlantısına tıklayarak hesabınızı aktif edin, ardından giriş yapabilirsiniz.",
          type: "success",
          buttonText: "Giriş Sayfasına Git",
        });
        navigate("/login");
      }
    } catch (err: any) {
      const errorMessage =
        err?.message ||
        err?.response?.data?.message ||
        "Kayıt işlemi sırasında bir hata oluştu.";
      setError(errorMessage);
      setSubmitting(false);
      resetTurnstile();
      await showAlert({
        title: "Kayıt Başarısız",
        message: errorMessage,
        type: "error",
        buttonText: "Tekrar Dene",
      });
    }
  };

  if (loading && !systemConfig) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <div className="mb-6 flex justify-center">
            <img
              src={systemConfig?.companyLogo || "/logo.svg"}
              alt={systemConfig?.companyName || "Logo"}
              className="h-24 w-24 object-cover rounded-full"
            />
          </div>

          <div className="flex items-center justify-center mb-2">
            <div className="text-3xl font-black tracking-tight text-gray-900">
              {systemConfig?.companyName ? (
                <span>{systemConfig.companyName}</span>
              ) : (
                <span>Yeni Eğitim Sistemi</span>
              )}
            </div>
          </div>

          <h2 className="text-lg font-medium text-gray-500 mb-4">
            Hesap Oluştur
          </h2>

          <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed">
            {systemConfig?.companyName || "Yeni Eğitim Sistemi"} platformuna katılmak için bilgilerinizi girin.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Kayıt Hatası</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Ad"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
            <input
              type="text"
              placeholder="Soyad"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
          </div>

          <div>
            <input
              type="email"
              placeholder="ornek@sirketiniz.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
          </div>

          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-gray-500 select-none pointer-events-none">
              <span className="text-base">🇹🇷</span>
              <span className="text-sm font-medium">+90</span>
            </div>
            <input
              type="tel"
              placeholder="5XX XXX XX XX"
              value={phone}
              onChange={handlePhoneChange}
              maxLength={13}
              required={isYesToolsDomain}
              className="w-full pl-[5.5rem] pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              disabled={submitting}
            />
          </div>

          {!departmentMode && (
          <div>
            {isYesToolsDomain && (
              <label className="mb-1 block text-xs font-medium text-gray-500">Doğum tarihi (isteğe bağlı)</label>
            )}
            <input
              type="date"
              value={birthDate}
              onChange={(e) => {
                const nextBirthDate = e.target.value;
                setBirthDate(nextBirthDate);

                if (selectedClassLevel && !normalizeYesToolsClassValue(selectedClassLevel)) {
                  setSelectedClassLevel("");
                  setSelectedGraduateTrack("");
                }
              }}
              max={new Date().toISOString().split("T")[0]}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium text-gray-700"
              required={!isYesToolsDomain}
              disabled={submitting}
            />
          </div>
          )}

          {/* Departman-seçimli mod: kayıtta görünecek departmanlar */}
          {departmentMode && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Departman</label>
              <select
                value={selectedDepartmentId}
                onChange={(e) => setSelectedDepartmentId(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium text-gray-700"
                required
                disabled={submitting}
              >
                <option value="">Departman Seçiniz</option>
                {registrationDepartments.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isYesToolsDomain && (
            <div>
              <input
                type="email"
                placeholder="Veli e-postası (isteğe bağlı — haftalık gelişim raporu için)"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
                disabled={submitting}
              />
            </div>
          )}

          {isYesToolsDomain && !departmentMode && (
            <div>
              <select
                value={selectedClassLevel}
                onChange={(e) => {
                  const nextClassLevel = e.target.value;
                  setSelectedClassLevel(nextClassLevel);

                  if (!isYesToolsGraduateTrackRequired(nextClassLevel)) {
                    setSelectedGraduateTrack("");
                  }
                }}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium text-gray-700"
                required
                disabled={submitting}
              >
                <option value="">Sınıf Seçiniz</option>
                {allowedClassOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {shouldShowGraduateTrackSelect && (
                <select
                  value={selectedGraduateTrack}
                  onChange={(e) => setSelectedGraduateTrack(e.target.value)}
                  className="mt-3 w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium text-gray-700"
                  required
                  disabled={submitting}
                >
                  <option value="">Alan Seçiniz</option>
                  {YES_TOOLS_GRADUATE_TRACK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              {classSelectionError && (
                <p className="mt-2 text-xs text-red-600">{classSelectionError}</p>
              )}
            </div>
          )}

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Şifre (En Az 8 Karakter)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 pr-12 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={submitting}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showRePassword ? "text" : "password"}
              placeholder="Şifre Tekrar"
              value={rePassword}
              onChange={(e) => setRePassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 pr-12 transition-all font-medium placeholder:text-gray-400"
              required
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowRePassword(!showRePassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={submitting}
            >
              {showRePassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div>
            <input
              type="text"
              placeholder="Davet kodu (varsa)"
              value={referralCode}
              maxLength={20}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium placeholder:text-gray-400"
              disabled={submitting}
            />
            {invitedCode && (
              <p className="mt-1.5 text-xs text-gray-500">Davetle geliyorsun; paket alırken davet indirimin hazır olacak.</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={kvkkOnay}
                onChange={(e) => setKvkkOnay(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                disabled={submitting}
                required
              />
              <span>
                <button type="button" onClick={() => setAydinlatmaAcik(true)} className="font-medium text-purple-700 underline underline-offset-2 hover:text-purple-900">
                  Aydınlatma Metni
                </button>
                'ni okudum. Kaydım, derslerim, ödevlerim ve akademiyle ilgili bilgilendirmelerin SMS, e-posta, WhatsApp ve telefonla
                {whatsapp.numara ? <> ({whatsapp.numara})</> : null} yapılacağını; telefon görüşmelerinin yapay zekâ desteğiyle
                yapılabileceğini ve kaydedildiğini biliyorum. <span className="text-red-600">*</span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={pazarlamaOnay}
                onChange={(e) => setPazarlamaOnay(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                disabled={submitting}
              />
              <span>Kampanya, indirim ve duyurulardan SMS ve e-postayla haberdar olmak istiyorum (isteğe bağlı).</span>
            </label>
          </div>
          <AydinlatmaPenceresi acik={aydinlatmaAcik} kapat={() => setAydinlatmaAcik(false)} />

          <div ref={turnstileRef} className="w-full" />

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all shadow-lg shadow-purple-600/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Kayıt yapılıyor...
              </div>
            ) : (
              "Kayıt Ol"
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <span className="text-gray-600">Zaten hesabın var mı? </span>
          <Link to="/login" className="text-purple-600 hover:text-purple-800 font-medium transition-colors">
            Giriş Yap
          </Link>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          <a
            href="https://yeniegitimsistemi.net/kullanici-sozlesmesi"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-purple-600 transition-colors"
          >
            Kullanıcı Sözleşmesi
          </a>
          <span className="mx-2">|</span>
          <a
            href="https://yeniegitimsistemi.net/gizlilik-politikasi"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-purple-600 transition-colors"
          >
            Gizlilik Politikası
          </a>
          <span className="mx-2">|</span>
          <a href="/aydinlatma-metni" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors">
            Aydınlatma Metni
          </a>
        </div>

      </div>
    </div>
  );
}
